/**
 * The concept's material set (concept L674–L701, L883–L886). Numbers are verbatim.
 */
import {
  AdditiveBlending,
  Color,
  DoubleSide,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  SpriteMaterial,
  type Texture,
} from 'three';
import type { Look } from '../world/looks.ts';

export interface TextureMaps {
  body: Texture | null;
  wing: Texture | null;
  dot: Texture | null;
  label: Texture | null;
  wake: Texture | null;
}

type Extra = Partial<
  Pick<MeshStandardMaterial, 'map' | 'envMapIntensity' | 'side' | 'vertexColors' | 'emissiveIntensity' | 'flatShading'>
> & {
  emissive?: number;
};

/** MeshStandardMaterial shorthand (concept `S`, L674). */
export function S(hex: number, metal: number, rough: number, extra: Extra = {}): MeshStandardMaterial {
  const m = new MeshStandardMaterial({ color: hex, metalness: metal, roughness: rough });
  const { emissive, ...rest } = extra;
  if (emissive !== undefined) m.emissive.setHex(emissive);
  Object.assign(m, rest);
  return m;
}

const ADD = { transparent: true, blending: AdditiveBlending, depthWrite: false } as const;

export function createMaterials(TX: TextureMaps, look: Look) {
  const map = (t: Texture | null) => (t ? { map: t } : {});
  const M = {
    fuselage: S(0xffffff, 0.55, 0.36, { ...map(TX.body), envMapIntensity: 1.3 }),
    wing: S(0xffffff, 0.5, 0.4, { ...map(TX.wing), envMapIntensity: 1.2 }),
    dark: S(0x2d3339, 0.55, 0.5),
    nozzle: S(0x3a3632, 0.85, 0.35, { side: DoubleSide }),
    core: new MeshBasicMaterial({ color: 0xff8a3a }),
    accent: S(0xf2a93b, 0.2, 0.45),
    red: S(0xb53b2e, 0.2, 0.5),
    missile: S(0xe6e6e2, 0.2, 0.4),
    glass: new MeshPhysicalMaterial({
      color: 0x3a2c10,
      metalness: 0.85,
      roughness: 0.06,
      clearcoat: 1,
      clearcoatRoughness: 0.03,
      envMapIntensity: 2.4,
    }),
    navR: new MeshBasicMaterial({ color: 0xff3b30 }),
    navG: new MeshBasicMaterial({ color: 0x3bff7a }),
    strobe: new MeshBasicMaterial({ color: 0xffffff }),
    flameOuter: new MeshBasicMaterial({ color: 0xff7a2a, opacity: 0.55, side: DoubleSide, ...ADD }),
    flameInner: new MeshBasicMaterial({ color: 0xffe0a0, opacity: 0.9, side: DoubleSide, ...ADD }),
    glow: new SpriteMaterial({ ...(TX.dot ? { map: TX.dot } : {}), color: 0xff9a40, opacity: 0.9, ...ADD }),
    heli: S(0x4b5347, 0.35, 0.55, { envMapIntensity: 1.1 }),
    blade: S(0x1f2326, 0.4, 0.6),
    disc: new MeshBasicMaterial({
      color: 0xcfd8dc,
      transparent: true,
      opacity: 0.09,
      depthWrite: false,
      side: DoubleSide,
    }),
    hull: S(0x39414a, 0.45, 0.5),
    deck: S(0x8e959b, 0.2, 0.7),
    cabin: S(0xd9dbd8, 0.15, 0.55),
    orange: S(0xdc6a24, 0.25, 0.55),
    white: S(0xeeebe4, 0.2, 0.5),
    tank: S(0xefece6, 0.35, 0.35, { envMapIntensity: 1.2 }),
    beacon: new MeshBasicMaterial({ color: 0xffb347 }),
    bridge: S(0xffffff, 0.25, 0.72, { vertexColors: true }),
    lamp: S(0xfff0d8, 0, 0.4, { emissive: 0xffc98a, emissiveIntensity: look.lamps }),
    tracer: new MeshBasicMaterial({ color: 0xffc35a, opacity: 1, ...ADD }),
    // Game layer (concept L884–L886).
    etracer: new MeshBasicMaterial({ color: 0xff4a2a, opacity: 1, ...ADD }),
    muzzle: new SpriteMaterial({ ...(TX.dot ? { map: TX.dot } : {}), color: 0xffb060, opacity: 1, ...ADD }),
    baked: S(0xffffff, 0.35, 0.55, { vertexColors: true, envMapIntensity: 1.0 }),
  };
  /** Enemy jet: the player jet's dark grey variant with red markings (GDD §3.6; concept L883). */
  const M2: typeof M = {
    ...M,
    fuselage: S(0x4a5056, 0.5, 0.42, map(TX.body)),
    wing: S(0x3a4046, 0.5, 0.45, map(TX.wing)),
    accent: S(0xc0392b, 0.2, 0.5),
    glass: S(0x151a20, 0.6, 0.15) as unknown as MeshPhysicalMaterial,
  };
  const labelMat = new MeshStandardMaterial({ ...(TX.label ? { map: TX.label } : {}), roughness: 0.6 });
  const wakeMat = new MeshBasicMaterial({
    ...(TX.wake ? { map: TX.wake } : {}),
    transparent: true,
    depthWrite: false,
    color: new Color(look.foam),
    opacity: 0.85,
  });
  return { M, M2, labelMat, wakeMat };
}

export type Materials = ReturnType<typeof createMaterials>['M'];
